import { selectAmendmentModal } from '../../features/actuarial/request-amendment/request-amendment.slice';
import { useDispatch, useSelector } from 'react-redux';
import SectionHeader from '../../components/ui/section-header';
import RequestAmendmentModal from '../../components/module/request-amendments/modal/request-amend/request-amendment-modal';
import ProposalTHead from '../../components/ui/proposal-thead';
import ProposalListTBody from '../../components/module/proposal-status/proposal-list-tbody';
import ComputationApprovalDataModal from '../../components/module/proposal-computation/modal/others/computation-approval-data-modal';
import { clearProposalId, clearTypeModal, clearTypeOfProposalStatus, clearUpdateTypeModal, setProposalId, setTypeModal } from '../../features/gms-state-and-modal/modal-slice';
import { resetBasicInformation } from '../../features/gms-proposal-forms/basic-information/basic-information.slice';
import { setCurrentStep } from '../../features/gms-proposal-forms/steps-channel-proposal_as/steps-channel-proposal_as.slice';
import { resetAllRates, resetRates } from '../../features/actuarial/rates-slice';
import { resetProposalForm } from '../../helper/proposal/proposal-reset-form';
import ProposalDetails from '../../components/module/proposal-status/modal/proposal-details';
import ComputationDetails from '../../components/module/proposal-computation/modal/others/computation-details';
import ConfirmDefaultModal from '../../components/global/modal/confirm-default-modal';
import { resetRemarks  } from '../../features/actuarial/remark-slice';
import { useComputationApprovalActions } from '../../hooks/actions/use-computation-approval-actions';
import { selectApproverRates, setApproverNotes, setTypeOfActionForApprover } from '../../features/actuarial/approval/approval-slice';
import { useForApprovalSocket } from '../../hooks/socket/use-for-approval-socket';
import Pagination from '../../components/ui/pagination';
import SearchFilterBar from '../../components/ui/search-filter-bar';
import { useProposalAllComputationAccess } from '../../hooks/access/use-proposal-all-computation-access';
import { proposalHeaderData } from '../../constant/gms-constant';
import { selectUserDepartmentCode } from '../../features/auth/auth-slice';
import { DEPARTMENT_CODES } from '../../constant/department-code-constant';
import { useModalAccess } from '../../components/global/modal/use-modal-access';

export default function AllRequestApprovalPage() {
    
    const { 
        isShowProposalDetails, 
        isShowComputationDetails,
        isShowActionProposalModal,
    } = useModalAccess()
    const userDepartmentCode = useSelector(selectUserDepartmentCode);
    const normalizedDepartmentCode = userDepartmentCode?.trim().toLowerCase();
    const canFetchApprovalList =
        normalizedDepartmentCode === DEPARTMENT_CODES.ACTUARIAL.trim().toLowerCase() ||
        normalizedDepartmentCode === DEPARTMENT_CODES.MKT_GMS.trim().toLowerCase();
    const amendmentModal = useSelector(selectAmendmentModal);

    const {
        isLoadingApprovalComputationList,
        isErrorApprovalComputationList,
        pagedData,
        setPage,
        handleReload,
        defaultTotalPages,
        handleChangePageSize,
        paginationDefaultPageSize,
        normalizedOptions,
        searchValue,
        setSearchValue,
        searchType,
        setSearchType,
        approvalComputationListResponse,
    } = useProposalAllComputationAccess({mode: 'team', canFetch: canFetchApprovalList})

    const handleViewProposal = (id: number, preparedById: number, status: string) => {
        dispatch(setProposalId(id));
        dispatch(setTypeModal('showActionProposalModal'))
    };

    const { 
        handleUpdateApproval,
        isLoadingUpdateApproval,
    } = useComputationApprovalActions();

    const dispatch = useDispatch();
    const handleCloseUpdateProposalModal = () => {
        dispatch(clearUpdateTypeModal());
        dispatch(resetBasicInformation());
        dispatch(clearProposalId());
        dispatch(clearTypeOfProposalStatus());
        dispatch(clearTypeModal());
        dispatch(setCurrentStep(1));
        dispatch(resetRates())
        dispatch(resetAllRates())
        resetProposalForm(dispatch);
        dispatch(resetRemarks())
    }

    const { typeOfAction, notes } = useSelector(selectApproverRates)
    const title = typeOfAction === 'pass' ? 'Approve Submission' : 'Deny Submission'
    const description = typeOfAction === 'pass' ? 'The submitted proposal details, computation, and supporting documents will be approved. Do you want to continue?' : 'The submitted proposal details, computation, and supporting documents proposal will be denied. Please provide a reason before continuing.'
    useForApprovalSocket()

    const tHeads: string[] = [
        proposalHeaderData.proposalStatus.label,
        proposalHeaderData.ratesStatus.label,
        proposalHeaderData.computedBy.label,
        proposalHeaderData.proposalAs.label,
        proposalHeaderData.groupName.label,
        proposalHeaderData.proposalType.label,
        proposalHeaderData.product.label,
        proposalHeaderData.preparedBy.label,
    ]

    return (
        <div className="flex h-full min-h-0 w-full flex-col bg-[#f8fafc] rounded-xl border border-gray-200">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <SectionHeader
                    title='Review Computation Rates'
                    subtitle='Review submitted computation rates, accept approved rates, or decline submissions with a reason for amendment.'
                    icon={
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 3h7l4 4v14H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 3v5h5" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="m9 15 2 2 4-4" />
                        </svg>
                    }
                />
                <div className="mx-auto flex min-h-0 w-full flex-1 flex-col gap-2">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white shadow-sm">
                        <div className="shrink-0 border-b border-gray-200 p-3">
                            <SearchFilterBar
                                searchType={searchType}
                                setSearchType={setSearchType}
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                                options={normalizedOptions}
                            />
                        </div>
                        {approvalComputationListResponse?.pagination && (
                            <div className="shrink-0 border-b border-gray-200 bg-gray-50/60 p-3">
                                <Pagination
                                    setPage={setPage}
                                    handleReload={handleReload}
                                    pagination={approvalComputationListResponse.pagination}
                                    defaultTotalPages={defaultTotalPages}
                                    setDefaultTotalPages={handleChangePageSize}
                                    totalPagesOptions={paginationDefaultPageSize}
                                />
                            </div>
                        )}
                        <div className="min-h-0 flex-1 overflow-auto">
                            <table className="min-w-max w-full border-collapse">
                                <ProposalTHead headers={tHeads} />
                                <ProposalListTBody
                                    headers={tHeads}
                                    typeOfIcon="compute"
                                    isLoadingGetProposal={isLoadingApprovalComputationList}
                                    isErrorGetProposal={isErrorApprovalComputationList}
                                    errorMessage={''}
                                    getProposal={pagedData}
                                    handleEdit={handleViewProposal}
                                />
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            {amendmentModal === 'showAmendmentDetails' && (
                <RequestAmendmentModal/>
            )}
            <ComputationApprovalDataModal
                isOpen={isShowActionProposalModal}
                onConfirm={() => dispatch(setTypeOfActionForApprover('pass'))}
                onDeny={() => dispatch(setTypeOfActionForApprover('deny'))}
                onClose={handleCloseUpdateProposalModal}
                isLoading={isLoadingUpdateApproval}
            />
            {(typeOfAction === 'pass' || typeOfAction === 'deny') && (
                <ConfirmDefaultModal
                    modalTitle={title}
                    modalSubTitle={description}
                    onCancel={() => dispatch(setTypeOfActionForApprover(null))}
                    onConfirm={handleUpdateApproval}
                    isLoading={isLoadingUpdateApproval}
                    isDeny={typeOfAction === 'deny' ? true : false}
                    onDeny={handleUpdateApproval}
                    notes={notes}
                    onNotesChange={(e) => dispatch(setApproverNotes(e.target.value))}
                />
            )}
            {isShowProposalDetails && ( <ProposalDetails /> )}
            {isShowComputationDetails && ( <ComputationDetails /> )}
        </div>
    )
}
